import { Box, MenuItem } from "@mui/material";
import {
  TextField,
  CustomSelect,
  DatePicker,
  CustomCheckbox,
} from "../../../../v2/MUI_custom_component";
import { OTPValidation } from "./OTPValidation";
import { useEffect, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  validateMobile,
  mobileInputValidation,
} from "../../../Common/Validation/mobileValidation";
import { encryptPassword } from "../../../../v2/Utils/Encryption/NodeForgeEncryption";
import { OTP_PURPOSE_ENUM } from "../../../Auth/Enum";
import { handleResendOTP } from "../API/Utils";
import { useSelector } from "react-redux";
import { getOwnerDetailsAction } from "../API/Action";
import { validateAge } from "../../../Common/OCR/utils/validation";

export const AddOwnerForm = ({
  selectedRole,
  firmId,
  onClose,
  fetchEmployeeDetails,
}) => {
  const [owners, setOwners] = useState([]);
  const [formData, setFormData] = useState({
    full_name: "",
    dob: "",
    phone_number: "",
    isWhatsAppSame: false,
  });
  const [validationErrors, setValidationErrors] = useState({
    full_name: "",
    dob: "",
    phone_number: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const userId = useSelector((state) => state.loginReducer.loginUser?.user_id);
  const encryptedUserId = encryptPassword(String(userId));

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setValidationErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const getOwnerDetailsFn = async () => {
    const { error, message, data } = await getOwnerDetailsAction(firmId);
    if (!error) {
      setOwners(data.owner_list);
      if (data.owner_list.length === 1) {
        setFormData((prev) => ({
          ...prev,
          full_name: data.owner_list[0].value,
        }));
      }
    } else {
      toast.error(message);
    }
  };

  const handleSendOTP = async () => {
    const mobileValidation = validateMobile(formData.phone_number);
    setValidationErrors((prev) => ({
      ...prev,
      phone_number: mobileValidation.valid ? "" : mobileValidation.message,
    }));
    if (!formData.dob) {
      setValidationErrors((prev) => ({
        ...prev,
        dob: "Date of birth is required",
      }));
    }
    if (!formData.full_name) {
      setValidationErrors((prev) => ({
        ...prev,
        full_name: "Please select an owner from the dropdown",
      }));
    }
    const ageValidation = validateAge(formData.dob, 18, 60, "employee");
    if (ageValidation.isValid === false) {
      setValidationErrors((prev) => ({
        ...prev,
        dob: ageValidation.error,
      }));
    }
    if (
      !mobileValidation.valid ||
      validationErrors.dob ||
      validationErrors.full_name ||
      !ageValidation.isValid
    )
      return;

    if (
      mobileValidation.valid &&
      validationErrors.dob === "" &&
      validationErrors.full_name === ""
    ) {
      setIsLoading(true);
      const payload = {
        mobile_number: encryptPassword(formData.phone_number),
        purpose: OTP_PURPOSE_ENUM.EMPLOYEE,
        user_id: encryptedUserId,
      };
      const { success } = await handleResendOTP(payload);
      setIsLoading(false);
      return success;
    }
  };

  const handleVerifySuccess = () => {
    try {
      // Reset form
      setFormData({
        full_name: "",
        dob: "",
        phone_number: "",
        isWhatsAppSame: false,
      });
      onClose();
    } catch (error) {
      console.error("Error refreshing employee data:", error);
      toast.error("Failed to refresh data. Please try again.");
    }
  };

  useEffect(() => {
    firmId && getOwnerDetailsFn();
  }, [firmId]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {owners.length === 1 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 0.5,
            }}>
            <TextField
              label="Owner"
              variant="standard"
              size="small"
              fullWidth
              fetchedState={true}
              value={owners[0].label}
            />
          </Box>
        ) : (
          <CustomSelect
            width={{ xs: "100%", md: "70%" }}
            label="Owner"
            value={formData.full_name}
            onChange={(e) => handleInputChange("full_name", e.target.value)}
            fullWidth
            error={!!validationErrors.full_name}
            helperText={validationErrors.full_name}>
            {owners.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </CustomSelect>
        )}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Date of Birth*"
            value={formData.dob ? dayjs(formData.dob) : null}
            onChange={(newValue) => {
              const dateString = newValue ? newValue.format("YYYY-MM-DD") : "";
              handleInputChange("dob", dateString);
            }}
            error={!!validationErrors.dob}
            helperText={validationErrors.dob}
            disableFuture
            format="DD/MM/YYYY"
            fullWidth
          />
        </LocalizationProvider>

        <TextField
          label="Mobile Number*"
          value={formData.phone_number}
          onChange={(e) =>
            handleInputChange(
              "phone_number",
              mobileInputValidation(e.target.value),
            )
          }
          placeholder="Enter Mobile number"
          error={!!validationErrors.phone_number}
          helperText={validationErrors.phone_number}
        />
      </Box>
      <CustomCheckbox
        label="Is above mobile number same as Whatsapp number?"
        checked={formData.isWhatsAppSame}
        onChange={() =>
          setFormData((prev) => ({
            ...prev,
            isWhatsAppSame: !prev.isWhatsAppSame,
          }))
        }
      />
      <OTPValidation
        mobileNumber={formData.phone_number}
        handleSendOTP={handleSendOTP}
        isLoading={isLoading}
        error={validationErrors}
        formData={formData}
        onCloseModal={onClose}
        onVerifySuccess={handleVerifySuccess}
        firmId={firmId}
        role={selectedRole}
        permissions={[]}
        refreshFunction={fetchEmployeeDetails}
      />
    </Box>
  );
};
